import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { FilePdf, DownloadSimple, CaretLeft, CaretRight, Lightning, X, PencilSimple } from "@phosphor-icons/react";

const LEAVE_SHORT = {
  ANNUAL: "DOP", SICK: "BP8", UNPAID: "NEOP", WFH: "DoD",
  EXTRAORDINARY: "IZR", COMPENSATORY: "KD", BLOOD_DONATION: "DAR", ESCORT: "SPR",
  OVERTIME: "OT", TIME_CORRECTION: "TC",
};
const LEAVE_OPTIONS = [
  { v: "", label: "—" },
  { v: "ANNUAL", label: "DOP · Dopust" },
  { v: "SICK", label: "BP8 · Bolniška" },
  { v: "EXTRAORDINARY", label: "IZR · Izredni dopust" },
  { v: "COMPENSATORY", label: "KD · Kompenzacija" },
  { v: "BLOOD_DONATION", label: "DAR · Krvodajalstvo" },
  { v: "ESCORT", label: "SPR · Spremstvo" },
  { v: "UNPAID", label: "NEOP · Neplačani dopust" },
];

function cellLabel(cell) {
  if (!cell) return "";
  if (cell.status?.startsWith("LEAVE:")) {
    const t = cell.status.split(":")[1];
    return LEAVE_SHORT[t] || t.slice(0, 3);
  }
  if (cell.status === "HOLIDAY") return "P";
  if (cell.status === "WEEKEND") return "—";
  if (cell.status === "WFH") return (cell.wfh_h || 0).toFixed(1);
  if (cell.status === "OFFICE") return (cell.worked_h || 0).toFixed(1);
  return "·";
}
function cellClass(cell) {
  if (!cell) return "bg-white";
  if (cell.status?.startsWith("LEAVE:")) return "bg-[#0047FF]/80 text-white font-bold";
  if (cell.status === "WEEKEND") return "bg-black/5 text-black/40";
  if (cell.status === "HOLIDAY") return "bg-[#FFCC00] text-black";
  if (cell.status === "WFH") return "bg-[#FF66B2]/80 text-black";
  if (cell.status === "OFFICE") return "bg-[#FFEE66]";
  if (cell.status === "ABSENT") return "bg-[#FF3333]/70 text-white";
  return "bg-white";
}

export default function MonthlyCardPage() {
  const { t } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [team, setTeam] = useState("");
  const [teams, setTeams] = useState([]);
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null); // {user_id, name, date, cell}
  const [groupOpen, setGroupOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year, month };
      if (team) params.team = team;
      const { data } = await api.get("/monthly_card/grid", { params });
      setGrid(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadTeams = async () => {
    try { const { data } = await api.get("/teams"); setTeams(data.teams || []); } catch {}
  };
  useEffect(() => { loadTeams(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, team]);

  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1);
  };

  const downloadPdf = async (user_id, name) => {
    try {
      const res = await api.get("/payroll/monthly-card.pdf", {
        params: { year, month, user_id }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Mesecni_karton_${name.replace(/\s+/g, "_")}_${year}_${String(month).padStart(2, "0")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const downloadAll = async () => {
    try {
      const res = await api.get("/payroll/monthly-card.pdf", { params: { year, month }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `Mesecni_karton_${year}_${String(month).padStart(2, "0")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (emp, d_iso) => {
    const cell = grid.cells[emp.user_id]?.[d_iso];
    setEdit({ user_id: emp.user_id, name: emp.name, date: d_iso, cell });
  };

  const saveEdit = async (payload) => {
    try {
      await api.post("/monthly_card/day", { user_id: edit.user_id, date: edit.date, ...payload });
      toast.success(t("mc_saved"));
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const monthName = new Date(year, month - 1, 1).toLocaleString(undefined, { month: "long" });

  return (
    <div data-testid="monthly-card-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="brut-border bg-white flex items-stretch">
            <button data-testid="mc-prev" onClick={prevMonth} className="px-3 py-2 border-r-2 border-black hover:bg-black hover:text-white"><CaretLeft size={18} weight="bold" /></button>
            <div className="px-5 py-2 flex items-center gap-2 min-w-[180px] justify-center">
              <select data-testid="mc-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="font-display font-black text-2xl tracking-tighter bg-transparent focus:outline-none">
                {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, "0")}</option>)}
              </select>
              <span className="font-display font-black text-2xl tracking-tighter">/</span>
              <select data-testid="mc-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="font-display font-black text-2xl tracking-tighter bg-transparent focus:outline-none">
                {[year - 2, year - 1, year, year + 1, year + 2].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button data-testid="mc-next" onClick={nextMonth} className="px-3 py-2 border-l-2 border-black hover:bg-black hover:text-white"><CaretRight size={18} weight="bold" /></button>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("team")}</label>
            <select data-testid="mc-team" value={team} onChange={(e) => setTeam(e.target.value)} className="brut-border p-2 bg-white">
              <option value="">{t("all_teams")}</option>
              {teams.map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <button
            data-testid="mc-group-entry"
            onClick={() => setGroupOpen(true)}
            className="bg-[#FFCC00] text-black brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
          >
            <Lightning size={16} weight="bold" /> {t("mc_group_entry")}
          </button>
        </div>
        <div className="flex items-end gap-2">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-black/60 mr-2">{t("mc_hint_click_cell")}</div>
          <button
            data-testid="mc-download-all"
            onClick={downloadAll}
            className="bg-[#FF8000] text-black brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider text-xs flex items-center gap-2"
          >
            <FilePdf size={16} weight="bold" /> {t("mc_download_all")}
          </button>
        </div>
      </div>

      <div className="brut-border bg-white brut-shadow overflow-auto max-h-[75vh]">
        <table className="min-w-full text-xs">
          <thead className="bg-[#F4F4F0] sticky top-0 z-10">
            <tr>
              <th className="text-left p-2 border-b-2 border-black whitespace-nowrap sticky left-0 bg-[#F4F4F0] z-20">
                {t("employee")}
              </th>
              {(grid?.days || []).map((d) => {
                const dayNum = Number(d.date.slice(-2));
                return (
                  <th
                    key={d.date}
                    className={`p-1 border-b-2 border-black text-center font-mono ${d.is_weekend ? "bg-black/10 text-black/50" : ""} ${d.is_holiday ? "bg-[#FFCC00]" : ""}`}
                    title={d.holiday_name || ""}
                  >
                    <div className="font-black text-sm">{dayNum}</div>
                    <div className="text-[10px] uppercase opacity-70">
                      {["Po", "To", "Sr", "Če", "Pe", "So", "Ne"][d.weekday]}
                    </div>
                  </th>
                );
              })}
              <th className="p-2 border-b-2 border-black text-right font-mono" title="Obveznost">OB</th>
              <th className="p-2 border-b-2 border-black text-right font-mono" title="Realizirano">T/R</th>
              <th className="p-2 border-b-2 border-black text-right font-mono" title="Saldo">+/-</th>
              <th className="p-2 border-b-2 border-black text-right font-mono" title="Stanje HH:MM">S(ZM)</th>
              <th className="p-2 border-b-2 border-black" />
            </tr>
          </thead>
          <tbody data-testid="mc-table">
            {(grid?.employees || []).map((emp) => (
              <tr key={emp.user_id} className="border-b border-black/10 hover:bg-[#FFCC00]/10">
                <td className="p-2 whitespace-nowrap sticky left-0 bg-white z-10" data-testid={`mc-row-${emp.employee_code || emp.user_id}`}>
                  <div className="font-bold">{emp.name}</div>
                  <div className="text-[10px] font-mono text-black/60">{emp.team || "—"} · {emp.employee_code || "—"}</div>
                </td>
                {(grid?.days || []).map((d) => {
                  const c = grid.cells[emp.user_id]?.[d.date];
                  const hasFlags = (c?.flags || []).length > 0;
                  const isOverride = c?.override;
                  return (
                    <td
                      key={d.date}
                      onClick={() => openEdit(emp, d.date)}
                      role="button"
                      data-testid={`mc-cell-${emp.user_id}-${d.date}`}
                      className={`p-1 text-center font-mono border-r border-black/5 cursor-pointer transition-colors ${cellClass(c)} ${hasFlags ? "ring-2 ring-[#FF3333] ring-inset" : ""} ${isOverride ? "underline decoration-[#0047FF] decoration-2" : ""} hover:brightness-90`}
                      title={`${c?.status || ""}${(c?.flags || []).length ? " · " + c.flags.join(", ") : ""}${isOverride ? " (manual)" : ""}`}
                    >
                      {cellLabel(c)}
                    </td>
                  );
                })}
                <td className="p-2 text-right font-mono">{emp.ob_h.toFixed(1)}</td>
                <td className="p-2 text-right font-mono font-bold">{emp.tr_h.toFixed(1)}</td>
                <td className={`p-2 text-right font-mono font-bold ${emp.bal_h < 0 ? "text-[#FF3333]" : emp.bal_h > 0 ? "text-[#00AA33]" : ""}`}>
                  {emp.bal_h > 0 ? "+" : ""}{emp.bal_h.toFixed(1)}
                </td>
                <td className={`p-2 text-right font-mono ${emp.bal_h < 0 ? "text-[#FF3333]" : emp.bal_h > 0 ? "text-[#00AA33]" : ""}`} data-testid={`mc-bal-${emp.user_id}`}>
                  {emp.bal_hhmm}
                </td>
                <td className="p-2">
                  <button
                    data-testid={`mc-pdf-${emp.user_id}`}
                    onClick={(e) => { e.stopPropagation(); downloadPdf(emp.user_id, emp.name); }}
                    className="brut-border bg-white brut-hover px-2 py-1 flex items-center gap-1 text-[11px] uppercase tracking-wider font-bold"
                  >
                    <DownloadSimple size={12} weight="bold" /> PDF
                  </button>
                </td>
              </tr>
            ))}
            {grid && grid.employees.length === 0 && (
              <tr><td colSpan={999} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs flex flex-wrap gap-3 items-center">
        <span className="font-bold uppercase tracking-wider">{t("legend")}:</span>
        <span className="tag bg-[#FFEE66]">{t("office")}</span>
        <span className="tag bg-[#FF66B2]/80">DoD</span>
        <span className="tag bg-[#0047FF] text-white">{t("leave")}</span>
        <span className="tag bg-[#FFCC00]">P (praznik)</span>
        <span className="tag bg-black/10 text-black/60">So/Ne</span>
        <span className="tag bg-[#FF3333] text-white">{t("anomaly")}</span>
        <span className="tag" style={{ textDecoration: "underline", textDecorationColor: "#0047FF" }}>{t("mc_override_tag")}</span>
      </div>

      {edit && (
        <DayEditDialog
          state={edit}
          onClose={() => setEdit(null)}
          onSave={saveEdit}
          onClear={() => saveEdit({ clear: true })}
        />
      )}
      {groupOpen && (
        <GroupEntryDialog
          year={year} month={month}
          teams={teams}
          employees={grid?.employees || []}
          onClose={() => setGroupOpen(false)}
          onSaved={() => { setGroupOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function DayEditDialog({ state, onClose, onSave, onClear }) {
  const { t } = useI18n();
  const [worked, setWorked] = useState(state.cell?.worked_h ?? "");
  const [wfh, setWfh] = useState(state.cell?.wfh_h ?? "");
  const [prihod, setPrihod] = useState("");
  const [odhod, setOdhod] = useState("");
  const [leave, setLeave] = useState(state.cell?.leave_type || "");
  const [note, setNote] = useState(state.cell?.note || "");

  const submit = (e) => {
    e.preventDefault();
    const payload = { note };
    if (prihod && odhod) {
      payload.arrival_time = prihod;
      payload.departure_time = odhod;
    } else {
      if (worked !== "" && worked !== null) payload.worked_h = Number(worked);
      if (wfh !== "" && wfh !== null) payload.wfh_h = Number(wfh);
    }
    if (leave) payload.leave_type = leave;
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form onSubmit={submit} data-testid="mc-edit-dialog" onClick={(e) => e.stopPropagation()} className="brut-border bg-white p-6 brut-shadow-lg max-w-md w-full max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-black/60">{t("mc_edit_day")}</div>
            <div className="font-display font-black text-2xl tracking-tighter">{state.name}</div>
            <div className="font-mono text-xs">{state.date}</div>
          </div>
          <button type="button" onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <div className="brut-border p-3 bg-[#FFF8DC] mb-4">
          <div className="text-[11px] font-bold uppercase tracking-wider mb-2">{t("mc_edit_times")}</div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("prihod")}</span>
              <input data-testid="mc-edit-prihod" type="time" value={prihod} onChange={(e) => setPrihod(e.target.value)} className="w-full brut-border p-2 font-mono" />
            </label>
            <label className="block">
              <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("odhod")}</span>
              <input data-testid="mc-edit-odhod" type="time" value={odhod} onChange={(e) => setOdhod(e.target.value)} className="w-full brut-border p-2 font-mono" />
            </label>
          </div>
          <div className="text-[10px] text-black/60 mt-1">{t("mc_edit_times_hint")}</div>
        </div>

        <div className="text-[11px] font-bold uppercase tracking-wider mb-2">{t("mc_edit_or_hours")}</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("mc_worked_h")}</span>
            <input data-testid="mc-edit-worked" type="number" step="0.25" min="0" max="24" value={worked} onChange={(e) => setWorked(e.target.value)} disabled={!!(prihod && odhod)} className="w-full brut-border p-2 font-mono disabled:bg-black/5" />
          </label>
          <label className="block">
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">DoD h</span>
            <input data-testid="mc-edit-wfh" type="number" step="0.25" min="0" max="24" value={wfh} onChange={(e) => setWfh(e.target.value)} disabled={!!(prihod && odhod)} className="w-full brut-border p-2 font-mono disabled:bg-black/5" />
          </label>
        </div>
        <label className="block mt-3">
          <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("leave_type")}</span>
          <select data-testid="mc-edit-leave" value={leave} onChange={(e) => setLeave(e.target.value)} className="w-full brut-border p-2 bg-white">
            {LEAVE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </label>
        <label className="block mt-3">
          <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("note")}</span>
          <input data-testid="mc-edit-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full brut-border p-2" />
        </label>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClear} data-testid="mc-edit-clear" className="brut-border bg-white brut-hover px-3 py-2 text-xs font-bold uppercase tracking-wider">{t("mc_clear_override")}</button>
          <button type="submit" data-testid="mc-edit-save" className="flex-1 bg-black text-white brut-border brut-shadow brut-hover px-4 py-2 font-bold uppercase tracking-wider">{t("save")}</button>
        </div>
      </form>
    </div>
  );
}

function GroupEntryDialog({ year, month, teams, employees, onClose, onSaved }) {
  const { t } = useI18n();
  const [team, setTeam] = useState("");
  const [startDate, setStartDate] = useState(`${year}-${String(month).padStart(2, "0")}-01`);
  const [endDate, setEndDate] = useState(`${year}-${String(month).padStart(2, "0")}-${new Date(year, month, 0).getDate()}`);
  const [workedH, setWorkedH] = useState("8");
  const [leaveType, setLeaveType] = useState("");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState([]);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [loading, setLoading] = useState(false);

  const filtered = team ? employees.filter(e => e.team === team) : employees;
  const toggle = (uid) => setSelected(s => s.includes(uid) ? s.filter(x => x !== uid) : [...s, uid]);
  const allSelected = selected.length === filtered.length && filtered.length > 0;
  const toggleAll = () => setSelected(allSelected ? [] : filtered.map(f => f.user_id));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        start_date: startDate, end_date: endDate,
        include_weekends: includeWeekends,
        note,
      };
      if (selected.length > 0) body.user_ids = selected;
      else if (team) body.team = team;
      else { toast.error(t("mc_group_pick_target")); setLoading(false); return; }
      if (workedH !== "") body.worked_h = Number(workedH);
      if (leaveType) body.leave_type = leaveType;
      const { data } = await api.post("/monthly_card/group_entry", body);
      toast.success(`${t("mc_group_applied")}: ${data.applied}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <form onSubmit={submit} data-testid="mc-group-dialog" onClick={(e) => e.stopPropagation()} className="brut-border bg-white p-6 brut-shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-black/60">{t("mc_group_entry")}</div>
            <div className="font-display font-black text-3xl tracking-tighter">{t("mc_group_title")}</div>
          </div>
          <button type="button" onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label>
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("start_date")}</span>
            <input data-testid="mc-group-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full brut-border p-2 font-mono" required />
          </label>
          <label>
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("end_date")}</span>
            <input data-testid="mc-group-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full brut-border p-2 font-mono" required />
          </label>
          <label>
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("mc_worked_h")}</span>
            <input data-testid="mc-group-worked" type="number" step="0.25" min="0" max="24" value={workedH} onChange={(e) => setWorkedH(e.target.value)} className="w-full brut-border p-2 font-mono" />
          </label>
          <label>
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("leave_type")}</span>
            <select data-testid="mc-group-leave" value={leaveType} onChange={(e) => setLeaveType(e.target.value)} className="w-full brut-border p-2 bg-white">
              {LEAVE_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </label>
          <label className="col-span-2">
            <span className="block text-xs font-bold uppercase tracking-wider mb-1">{t("note")}</span>
            <input data-testid="mc-group-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full brut-border p-2" />
          </label>
          <label className="col-span-2 flex items-center gap-2 brut-border bg-[#F4F4F0] p-2 text-xs font-bold uppercase tracking-wider">
            <input data-testid="mc-group-include-weekends" type="checkbox" checked={includeWeekends} onChange={(e) => setIncludeWeekends(e.target.checked)} />
            {t("mc_include_weekends")}
          </label>
        </div>

        <div className="brut-border p-3 bg-[#F4F4F0] mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-bold uppercase text-xs tracking-wider">{t("mc_group_targets")}</div>
            <div className="flex items-center gap-2">
              <select data-testid="mc-group-team" value={team} onChange={(e) => { setTeam(e.target.value); setSelected([]); }} className="brut-border p-1 bg-white text-xs">
                <option value="">{t("all_teams")}</option>
                {teams.map(x => <option key={x} value={x}>{x}</option>)}
              </select>
              <button type="button" data-testid="mc-group-toggle-all" onClick={toggleAll} className="brut-border bg-white px-2 py-1 text-xs font-bold uppercase tracking-wider">
                {allSelected ? t("deselect_all") : t("select_all")}
              </button>
            </div>
          </div>
          <div className="max-h-[220px] overflow-y-auto grid grid-cols-2 gap-1">
            {filtered.map((e) => (
              <label key={e.user_id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-1 py-0.5">
                <input data-testid={`mc-group-emp-${e.user_id}`} type="checkbox" checked={selected.includes(e.user_id)} onChange={() => toggle(e.user_id)} />
                <span className="truncate">{e.name} <span className="text-[10px] text-black/60">· {e.team}</span></span>
              </label>
            ))}
            {filtered.length === 0 && <div className="text-xs text-black/60 col-span-2 p-2">{t("no_data")}</div>}
          </div>
          <div className="text-[10px] mt-2 text-black/60">
            {selected.length > 0
              ? `${selected.length} ${t("selected")}`
              : team ? t("mc_whole_team_hint") : t("mc_group_pick_target")}
          </div>
        </div>

        <button type="submit" disabled={loading} data-testid="mc-group-submit" className="w-full bg-black text-white brut-border brut-shadow brut-hover px-4 py-3 font-bold uppercase tracking-wider disabled:opacity-60">
          {loading ? "…" : t("mc_group_apply")}
        </button>
      </form>
    </div>
  );
}
